import { createContext, useCallback, useContext, useMemo, useRef, useState, type ReactNode } from 'react'
import { CheckCircle2, Info, X, XCircle } from 'lucide-react'

type ToastKind = 'success' | 'error' | 'info'
type Toast = { id: number; kind: ToastKind; title: ReactNode; body?: ReactNode }

type ToastContextValue = {
  toast: (kind: ToastKind, title: ReactNode, body?: ReactNode) => void
}

const ToastContext = createContext<ToastContextValue | null>(null)

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([])
  const nextId = useRef(1)
  const dismiss = useCallback((id: number) => setToasts((all) => all.filter((t) => t.id !== id)), [])
  const toast = useCallback(
    (kind: ToastKind, title: ReactNode, body?: ReactNode) => {
      const id = nextId.current++
      setToasts((all) => [...all.slice(-3), { id, kind, title, body }])
      window.setTimeout(() => dismiss(id), kind === 'error' ? 10_000 : 6_000)
    },
    [dismiss],
  )
  const value = useMemo(() => ({ toast }), [toast])
  return (
    <ToastContext.Provider value={value}>
      {children}
      {/* a phone shows them across the top (the in-progress bar lives at the bottom there); a desktop stacks them bottom right (Nov 4 2026) */}
      <div className="pointer-events-none fixed inset-x-4 top-[max(1rem,env(safe-area-inset-top))] z-[60] flex flex-col gap-2 sm:inset-x-auto sm:bottom-4 sm:right-4 sm:top-auto sm:w-80">
        {toasts.map((t) => (
          <div
            key={t.id}
            role="status"
            className="pointer-events-auto card flex items-start gap-3 p-3 shadow-2xl animate-[toast-down_180ms_ease-out] sm:animate-[toast-in_160ms_ease-out]"
          >
            {t.kind === 'success' ? (
              <CheckCircle2 className="mt-0.5 size-5 shrink-0 text-accent" />
            ) : t.kind === 'error' ? (
              <XCircle className="mt-0.5 size-5 shrink-0 text-negative" />
            ) : (
              <Info className="mt-0.5 size-5 shrink-0 text-muted" />
            )}
            <div className="min-w-0 flex-1">
              <div className="text-sm font-medium">{t.title}</div>
              {t.body ? <div className="mt-0.5 text-xs text-muted">{t.body}</div> : null}
            </div>
            <button type="button" className="btn btn-ghost !p-0.5" onClick={() => dismiss(t.id)} aria-label="Dismiss">
              <X className="size-4" />
            </button>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  )
}

export function useToast() {
  const ctx = useContext(ToastContext)
  if (!ctx) throw new Error('useToast outside ToastProvider')
  return ctx.toast
}
